/**
 * English bundled fallback for i18n when remote `icua-i18n` is unavailable.
 * Keys mirror `zh-cn-fallback/namespaces.ts` for consistent lookups.
 *
 * @module locales/en-fallback/namespaces
 */

/** Namespace map aligned with zh-cn-fallback structure */
export const enFallbackNamespaces: Record<string, Record<string, unknown>> = {
  Header: {
    home: 'Home',
    'news center': 'News',
    'Flagship Initiatives': 'Flagship Initiatives',
    '未来开创者——可持续发展主题创意征集活动': 'Future Creators - Creative Campaign on Sustainable Development',
    非物质文化遗产教育教师专业发展项目: 'Teacher Professional Development Program in Intangible Cultural Heritage Education',
    'smart education platform': 'E4T Smart Education',
    'UNESCO ASPnet': 'UNESCO ASPnet',
    '2026 aspnet global conference': '2026 ASPnet Global Conference',
    partners: 'Partners',
    'about us': 'About Us',
    search: 'Search',
    'search placeholder': 'Enter keywords',
    'close search': 'Close search',
  },

  Footer: {
    'organization full name': 'International Centre for UNESCO ASPnet',
    'contact section title': 'Contact Us',
    'copyright rights': '© 2026 ICUA. All rights reserved.',
    'copyright credit': 'Powered by Huashi Cloud Intelligence',
    /** @deprecated Prefer copyright rights + copyright credit */
    'copyright line': '© 2026 ICUA. All rights reserved. Powered by Huashi Cloud Intelligence',
    'apply now': 'Contact Us',
    list: [
      { title: 'Email', href: 'info@icua.org', hrefType: 'mailto' },
      { title: 'Phone', href: '+86-899-88344287', hrefType: 'tel' },
      {
        title: 'Address',
        href: 'Building 11, Yonyou Industrial Park (Sanya), Xindao Street, Creative Industrial Park, Yazhou District, Sanya, Hainan, China',
      },
    ],
  },

  Index: {
    'home hero banner alt': 'ICUA homepage hero',
    'home hero subtitle': 'Connecting schools worldwide to advance sustainable education',
    'home hero meeting detail': 'View Conference Details',
    'home hero meeting video': 'Watch Conference Videos',
    'home hero activity detail': 'view more',
    'home hero activity detail url': 'https://www.icua-cipp.com/en/activity/2026',
    'home hero live gallery': 'Live Gallery',
    'home hero live gallery url': 'https://m.alltuu.com/topic/view/1030260608?from=link',
    news: 'News',
    home: {
      newsTitle: 'News',
      activityTitle: 'Global Festival Greetings Poster',
    },
    'more news': 'View more',
    'read full article': 'Read More',
    'home international posters': 'Global Festival Greetings Poster',
    'home center intro title': 'Centre Introduction',
    'home center intro paragraph 1':
      'The International Centre for UNESCO ASPnet (ICUA), a UNESCO Category 2 Centre based in Hainan, China, was established in 2021 to strengthen the UNESCO Associated Schools Network (ASPnet) through international collaboration and innovation.',
    'home center intro paragraph 2':
      'ICUA serves the global education community through four core functions: knowledge production, capacity development, technical support, and information sharing. Our work directly contributes to Sustainable Development Goal 4 (SDG 4) and the Education 2030 Agenda.',
    'home center intro paragraph 3':
      'Established in 1953, ASPnet is UNESCO\'s flagship education programme, connecting nearly 10,000 schools across over 170 countries. ASPnet schools are laboratories for innovative pedagogy in peace education, intercultural dialogue, sustainable development, and global citizenship.',
    // 旧第四段已并入三段结构，保留 key 避免平台配置报缺
    'home center intro paragraph 4': '',
    'home center intro video cover alt': 'Centre introduction video cover',
    'home center intro play': 'Play centre introduction video',
    'home center intro video title': 'Centre introduction video',
    'home center intro video url': 'https://static.huashicloud.com/assets/ka/icua/video/center-intro-en1.mov',
    'home center intro video unsupported': 'Your browser does not support video playback. Please upgrade your browser and try again.',
    'smart education platform': 'E4T Smart Education Platform',
    'smart education platform desc':
      'The "Education for Tomorrow (E4T) Smart Education Platform" is a project independently designed, developed, and operated by ICUA. Its primary aim is to foster the development of communities of practice by leveraging educational innovation and international collaboration. The platform aligns with the United Nations 2030 Agenda for Sustainable Development, particularly SDG 4, while addressing related goals such as gender equality, good health and well-being, climate action, education for peace and development, protection of cultural heritage, and intercultural understanding.\n\nThrough the integration of global-quality educational resources, the construction of a digital collaboration network, and the facilitation of school transformations and teacher professional development, the platform provides educators with a comprehensive digital tool to advance knowledge sharing and cross-cultural cooperation.',
    'visit platform': 'Learn more',
    'smart education platform diagram alt': 'E4T platform diagram',
    'smart education hero headline':
      'Through educational transformation, support holistic student development and well-being for a sustainable future.',
    'home hero background image': 'home/icua-home-en1.jpeg',
    /** 年会视频弹窗（MeetingVideoButton，Index 命名空间） */
    common: {
      cancel: 'Cancel',
      close: 'Close',
      confirm: 'Confirm',
    },
    'home meeting video empty': 'Resources will be updated during the conference. Please check back later.',
    'home meeting video date prefix': 'Date: ',
    'home meeting video error message': 'Failed to load the video. Please try again later.',
    'home meeting video error title': 'Load failed',
    'home meeting video got it': 'OK',
    'home meeting video modal title': 'Conference video',
    'home meeting video open new tab': 'If the player does not work, open the link below in a new tab.',
    'home meeting video password desc': 'Enter the password to watch the conference video.',
    'home meeting video password error': 'Incorrect password. Please try again.',
    'home meeting video password placeholder': 'Password',
    'home meeting video password title': 'Enter viewing password',
    'home meeting video tips title': 'Notice',
    'home meeting video unknown date': 'Date not specified',
  },

  Partners: {
    'contact us': 'Contact us',
    'join us': 'Join Us',
    'partners join us tip': 'To apply for partnership, please submit your information via the “Contact us” entry at the bottom.',
    partnersSectionUniversities: 'Universities',
    partnersSectionFutureCreators:
      '2024-CIPP Partners',
    partnersSubNavAria: 'Partner categories',
    'click to enter the official website': 'Visit official website',
    /** University partner cards — hover CTA */
    'view more': 'View more',
  },

  'About Us': {
    'about us': 'About Us',
    aboutIntro: 'Introduction',
    visionAndMission: 'Vision And Mission',
    functions: 'Functions',
    membersIntro: 'Governing Board Members',
    vision: 'Vision',
    'vision desc': 'Empowering Global Student Well-being Through Transformative Education for a Sustainable Future',
    mission: 'Mission',
    missionFeatures: [],
    missionCardFallback1:
      'The International Centre for UNESCO ASPnet (ICUA) advances global student well-being by fostering Education for Sustainable Development (ESD), cross-cultural learning, and social inclusion.',
    missionCardFallback2:
      'Through capacity development, youth engagement, research, and international cooperation, ICUA empowers students, educators, and communities to co-create peaceful, inclusive and sustainable futures.',
    'platform introduction desc':
      'The International Centre for UNESCO ASPnet (ICUA), a UNESCO Category 2 Centre based in Hainan, China, was established in 2021 to strengthen the UNESCO Associated Schools Network (ASPnet) through international collaboration and innovation.\n\nICUA serves the global education community through four core functions: knowledge production, capacity development, technical support, and information sharing. Our work directly contributes to Sustainable Development Goal 4 (SDG 4) and the Education 2030 Agenda.\n\nEstablished in 1953, ASPnet is UNESCO\'s flagship education programme, connecting nearly 10,000 schools across over 170 countries. ASPnet schools are laboratories for innovative pedagogy in peace education, intercultural dialogue, sustainable development, and global citizenship.',
    functionsList: [
      {
        title: 'Technical support',
        image: 'about-us/support-icon.svg',
        descs: [
          'ICUA will provide policy support, professional and technical assistance to schools, and will encourage exchange among ASPnet students and teachers through educational activities such as summer/winter camps, summer schools, short-term study tours, visiting programs, and the Information Technology Application Training (ITAT).',
        ],
      },
      {
        title: 'Information sharing',
        image: 'about-us/share-icon.svg',
        descs: [
          'ICUA will develop an online resource and information platform using cloud computing, big data, and artificial intelligence, and will organize the annual ASPnet International Forum to promote the circulation and exchange of successful ASPnet experiences and best practices',
        ],
      },
      {
        title: 'Knowledge production',
        image: 'about-us/knowledge-icon.svg',
        descs: ['Research will be conducted on the emerging needs and trends of schooling in UNESCO Member States.'],
      },
      {
        title: 'Capacity development',
        image: 'about-us/develop-icon.svg',
        descs: [
          'With a specific focus on the Asia-Pacific region, ICUA will carry out training programs on ASPnet-relevant topics for ASPnet stakeholders from primary and secondary schools.',
        ],
      },
    ],
    governingList: [
      {
        department: 'Chair of the Governing Board',
        members: [{ position: 'Vice Governor, Hainan Provincial People’s Government', name: 'XIE Jing' }],
      },
      {
        department: 'Governing Board Members',
        members: [
          {
            position: 'Representative of the UNESCO Director-General, UNESCO ASPnet International Coordinator',
            name: 'Lydia Ruprecht',
          },
          {
            position: 'Secretary-General, Chinese National Commission for UNESCO',
            name: 'QIN Changwei',
          },
          {
            position: 'Director-General, Hainan Provincial Department of Education',
            name: 'DING Hui',
          },
          {
            position: 'Vice Mayor, People\'s Government of Sanya City, Hainan Province',
            name: 'YIN Chengling',
          },
          {
            position: 'Education Programme Officer, UNESCO Cairo Office, Egypt',
            name: 'Robert Parua',
          },
          {
            position: 'UNESCO ASPnet National Coordinator, Oman',
            name: 'Yousriya Sulaiman AlHarthi',
          },
          {
            position: 'UNESCO Chair in Comparative Education, The University of Hong Kong',
            name: 'Mark Bray',
          },
          {
            position: 'Professor, Central China Normal University',
            name: 'LIU Yanshen',
          },
          {
            position: 'Secretary of the Party Committee, Hainan Middle School',
            name: 'LIU Haiying',
          },
          {
            position: 'President, Hainan Educational Research & Training Institute',
            name: 'SUN Ziqiang',
          },
          {
            position: 'Director, International Centre for UNESCO ASPnet',
            name: 'YU Lizhong',
          },
        ],
      },
    ],
    aboutTeams: [
      {
        department: 'Leadership',
        members: [
          { position: 'Director', name: 'YU Lizhong' },
          { position: 'Executive Director', name: 'ZHANG Wei' },
        ],
      },
      {
        department: 'Office for Education for Sustainable Development and Innovation',
        members: [
          { position: 'Manager', name: 'JIA Wenjuan' },
          { position: 'Project Coordinator', name: 'YU Pu' },
          { position: 'Project Assistant', name: 'PAN Xueyu' },
        ],
      },
      {
        department: 'Office for Intercultural and Heritage Education',
        members: [
          { position: 'Manager', name: 'LI Xingfang' },
          { position: 'Project Coordinator', name: 'LIANG Xi' },
          { position: 'Project Assistant', name: 'YANG Yingxue' },
        ],
      },
      {
        department: 'Office for Strategic Planning and Administration',
        members: [
          { position: 'Manager', name: 'GAO Yuan' },
          { position: 'Project Coordinator', name: 'DU Ying' },
        ],
      },
    ],
    aboutAcademicGuidanceUnitList: [
      { title: 'The Institute of Curriculum and Instruction, East China Normal University, Shanghai', href: '' },
      { title: 'ICUA Fellow', href: '' },
    ],
    councilMembersListTitle: 'List of Governing Board Members (Second Session)',
    aboutTeamSectionTitle: 'About the Team',
    aboutTeamIntro:
      'ICUA is supported by a professional team committed to global education, cultural exchange and sustainable development.',
    aboutAcademicGuidanceUnit: 'Academic guidance',
    internBackgroundCheck: 'Internship Verification',
    internBackgroundCheckUrl: '',
    internBackgroundCheckDesc:
      'For authorized third parties—such as employers, academic institutions, or credentialing agencies—who require verification of an individual\'s internship at the International Centre for UNESCO ASPnet (ICUA), please contact the ICUA Office for Strategic Planning and Administration through the official channels below:',
    internBackgroundCheckContact: 'Contact',
    internBackgroundCheckEmailLabel: 'Official email:',
    internBackgroundCheckPhoneLabel: 'Phone:',
    internBackgroundCheckTip:
      '@All verification requests must be submitted with the intern\'s full name, internship period, and a brief explanation of the purpose of the inquiry.',
    'mission desc':
      'The International Centre for UNESCO ASPnet (ICUA) advances global student well-being by fostering Education for Sustainable Development (ESD), cross-cultural learning, and social inclusion.\n\nThrough capacity development, youth engagement, research, and international cooperation, ICUA empowers students, educators, and communities to co-create peaceful, inclusive and sustainable futures.',
    functionsDesc: 'Educational exchange, capacity building and global cooperation programmes.',
    'about us hot info': 'Highlights',
    'about us fill in questionnaire': 'Fill in the registration form',
  },

  Conference: {
    'conference hero title': '2026 ASPnet Global Conference',
    'conference hero subtitle': 'ICUA Global ASPnet Conference 2026',
    'conference home': 'Conference home',
    'conference agenda': 'Provisional agenda',
    'conference session': 'Information & guidelines',
    'conference posters': 'Posters',
    'conference section title': 'Conference information',
    'conference posters section title': 'Conference posters',
    'conference mobile placeholder':
      'H5 conference hub: open a tab to view a short summary of that section.',
    'conference agenda section title': 'Provisional agenda',
    'conference agenda summary': 'View the schedule and key milestones.',
    'conference session section title': 'Information & guidelines',
    'conference session summary': 'Practical information, guidelines and contacts.',
    'conference posters summary': 'Posters from schools and organizations worldwide.',
    'conference disclaimer title': 'Disclaimer',
    'conference disclaimer text':
      'Views expressed in posters are those of the authors and do not necessarily reflect the positions of ASPnet or ICUA.',
  },

  Global: {
    'not found': 'Page not found',
    'uncatch error': 'An unexpected error occurred',
    'back to home': 'Back to home',
    'no data': 'No data',
    confirm: 'Confirm',
  },

  LinkPagination: {
    'prev page': 'Previous page',
    'next page': 'Next page',
    pageSize: '{{pageSize}} per page',
    total: '{{total}} items in total',
  },

  translation: {
    collectForm: {
      success: 'Submitted successfully. Thank you for your feedback!',
    },
    'collectForm.success': 'Submitted successfully. Thank you for your feedback!',
    common: {
      pleaseSelect: 'Please select',
      submit: 'Submit',
      submitting: 'Submitting…',
    },
    'common.pleaseSelect': 'Please select',
    'common.submit': 'Submit',
    'common.submitting': 'Submitting…',
  },

  'News Center': {
    'news center': 'News Center',
    'activity time': 'Event time',
  },

  Activity: {
    'international conference and activities': 'International conferences & events',
    'view details': 'View details',
  },

  Search: {
    title: 'Search results',
    resultCount: '{{count}} results found',
  },

  Database: {
    title: 'Knowledge base',
    download: 'Download',
  },

  'Smart Education Platform': {
    'E4T intro card title': 'About the E4T Smart Education Platform',
    'E4T intro body':
      'The "Education for Tomorrow (E4T) Smart Education Platform" is a project independently designed, developed, and operated by ICUA. Its primary aim is to foster the development of communities of practice by leveraging educational innovation and international collaboration.\n\n The platform a ligns with the United Nations 2030 Agenda for Sustainable Development, particularly SDG 4, while addressing related goals such as gender equality, good health and well-being, climate action, education for peace and development, protection of cultural heritage, and intercultural understanding.\n\n Through the integration of global-quality educational resources, the construction of a digital collaboration network, and the facilitation of school transformations and teacher professional development, the platform provides educators with a comprehensive digital tool to advance knowledge sharing and cross-cultural cooperation.',
    'platform intro cover': 'smart-education-platform/e4t-intro.jpg',
    'smart education hero headline': 'Empowering Global Student Well-being Through Transformative Education for a Sustainable Future',
    'visit E4T platform cta': 'Visit E4T Smart Education Platform ',
    'platform intro section title': 'About the Platform',
    'platform functions section title': 'Platform features',
    coreFunctions: [],
    'feature card 1 title': 'Smart connection',
    'feature card 1 desc': 'Building a digital collaboration network for educators and institutions.',
    'feature card 1 cover': 'smart-education-platform/e4t-feature-connect.png',
    'feature card 1 icon': 'smart-education-platform/e4t-connect-icon.svg',
    'feature card 2 title': 'Resource sharing',
    'feature card 2 desc': 'Aggregating high-quality educational resources worldwide to promote educational equity across regions.',
    'feature card 2 cover': 'smart-education-platform/e4t-feature-share.png',
    'feature card 2 icon': 'smart-education-platform/e4t-share-icon.svg',
    'feature card 3 title': 'Policy & trends',
    'feature card 3 desc': 'Supporting educators and policy makers in tracking global education policies and development trends.',
    'feature card 3 cover': 'smart-education-platform/e4t-feature-policy.png',
    'feature card 3 icon': 'smart-education-platform/e4t-policy-icon.svg',
    'feature card 4 title': 'Professional support',
    'feature card 4 desc': 'Providing teacher training, school partnerships, and leadership development opportunities.',
    'feature card 4 cover': 'smart-education-platform/e4t-feature-support.png',
    'feature card 4 icon': 'smart-education-platform/e4t-support-icon.svg',
  },
};
